import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../swof/api";
import { AdminShell } from "./ProtectedRoute";
import { InlineEditor, RichEditor, veldStijl } from "./RichText";

function alsTekst(html = "") {
  const el = document.createElement("div");
  el.innerHTML = String(html).replace(/<br\s*\/?>/gi, " ");
  return (el.textContent || "").trim();
}

function Melding({ soort, children, testId }) {
  const goud = soort === "let-op";
  return (
    <div data-testid={testId} style={{
      marginTop: 10, padding: "10px 13px", fontFamily: "var(--sans)", fontSize: 13, lineHeight: 1.5,
      border: `1px solid ${goud ? "rgba(201,169,97,0.55)" : "rgba(201,80,60,0.55)"}`,
      background: goud ? "rgba(201,169,97,0.08)" : "rgba(201,80,60,0.08)",
      color: goud ? "var(--gold-bright)" : "#e8b0a4",
    }}>{children}</div>
  );
}

function Label({ veld }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
      <span className="eyebrow" style={{ fontSize: 10, color: "var(--gold)" }}>{veld.label}</span>
      {veld.verplicht && (
        <span style={{ fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.16em", color: "#e8b0a4" }}>
          verplicht
        </span>
      )}
    </div>
  );
}

function Teller({ veld, waarde }) {
  if (!veld.limiet) return null;
  const n = alsTekst(waarde).length;
  const over = n > veld.limiet;
  return (
    <span data-testid={`teller-${veld.naam}`} style={{
      fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.14em",
      color: over ? "#e8b0a4" : "rgba(243,234,215,0.45)",
    }}>{n} / {veld.limiet}{over ? " — te lang voor het ontwerp" : ""}</span>
  );
}

function Paren({ veld, waarde, onChange }) {
  const rijen = waarde || [];
  const zet = (i, k, v) => {
    const nieuw = rijen.map((r, j) => (j === i ? { ...r, [k]: v } : r));
    onChange(nieuw);
  };
  return (
    <div>
      {rijen.map((rij, i) => (
        <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "center" }}>
          {veld.sub.map(([k, sublabel]) => (
            <input key={k} value={rij[k] || ""} placeholder={sublabel}
              data-testid={`veld-${veld.naam}-${i}-${k}`}
              onChange={(e) => zet(i, k, e.target.value)}
              style={{ ...veldStijl, minHeight: 44, fontSize: 15, flex: k === "k" || k === "tijd" ? "0 0 190px" : 1 }} />
          ))}
          <button type="button" data-testid={`verwijder-${veld.naam}-${i}`}
            onClick={() => onChange(rijen.filter((_, j) => j !== i))} style={{
              fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.16em", color: "#e8b0a4",
              border: "1px solid rgba(201,80,60,0.4)", padding: "10px 12px", background: "transparent",
            }}>Weg</button>
        </div>
      ))}
      <button type="button" data-testid={`toevoegen-${veld.naam}`}
        onClick={() => onChange([...rijen, Object.fromEntries(veld.sub.map(([k]) => [k, ""]))])}
        style={{
          fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
          color: "var(--gold)", border: "1px solid rgba(201,169,97,0.4)", padding: "10px 14px",
          background: "transparent",
        }}>Regel toevoegen</button>
    </div>
  );
}

function Veld({ veld, waarde, onChange, media, videos, afgestemd, verboden }) {
  const testId = `veld-${veld.naam}`;
  const tekst = alsTekst(waarde);
  const raakt = veld.soort === "rich" || veld.soort === "inline" || veld.soort === "tekst";
  const letOp = raakt ? afgestemd.filter((a) => tekst.toLowerCase().includes(a.fragment.toLowerCase())) : [];
  const fouten = raakt ? verboden.filter((v) => tekst.toLowerCase().includes(v.fout.toLowerCase())) : [];

  let veldElement;
  if (veld.soort === "rich") {
    veldElement = <RichEditor waarde={waarde || ""} onChange={onChange} testId={testId} />;
  } else if (veld.soort === "inline") {
    veldElement = <InlineEditor waarde={waarde || ""} onChange={onChange} testId={testId} />;
  } else if (veld.soort === "bool") {
    veldElement = (
      <label style={{ display: "flex", alignItems: "center", gap: 12, fontFamily: "var(--sans)", fontSize: 14 }}>
        <input type="checkbox" checked={Boolean(waarde)} data-testid={testId}
          onChange={(e) => onChange(e.target.checked)} />
        <span style={{ opacity: 0.75 }}>{waarde ? "Aan" : "Uit"}</span>
      </label>
    );
  } else if (veld.soort === "getal") {
    veldElement = (
      <input type="number" value={waarde ?? ""} data-testid={testId}
        onChange={(e) => onChange(e.target.value)} style={{ ...veldStijl, minHeight: 48, maxWidth: 200 }} />
    );
  } else if (veld.soort === "keuze") {
    veldElement = (
      <select value={waarde ?? ""} data-testid={testId} onChange={(e) => onChange(e.target.value)}
        style={{ ...veldStijl, minHeight: 48, maxWidth: 320 }}>
        {veld.opties.map(([v, l]) => <option key={v} value={v} style={{ color: "#111" }}>{l}</option>)}
      </select>
    );
  } else if (veld.soort === "media" || veld.soort === "video") {
    const lijst = veld.soort === "media" ? media : videos;
    veldElement = (
      <select value={waarde || ""} data-testid={testId}
        onChange={(e) => onChange(e.target.value || null)}
        style={{ ...veldStijl, minHeight: 48, maxWidth: 460 }}>
        <option value="" style={{ color: "#111" }}>— geen —</option>
        {lijst.map((it) => (
          <option key={it.id} value={it.id} style={{ color: "#111" }}>
            {veld.soort === "media" ? it.bestandsnaam : `${it.bron} · ${it.externe_id} — ${it.titel}`}
          </option>
        ))}
      </select>
    );
  } else if (veld.soort === "media_lijst") {
    const gekozen = waarde || [];
    veldElement = (
      <div data-testid={testId} style={{
        maxHeight: 260, overflowY: "auto", border: "1px solid var(--line)", padding: 12,
      }}>
        {media.length === 0 && <span style={{ opacity: 0.5, fontSize: 13 }}>Nog geen media geüpload.</span>}
        {media.map((m) => (
          <label key={m.id} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "6px 0",
            fontFamily: "var(--sans)", fontSize: 13,
          }}>
            <input type="checkbox" checked={gekozen.includes(m.id)}
              data-testid={`veld-${veld.naam}-${m.id}`}
              onChange={(e) => onChange(e.target.checked
                ? [...gekozen, m.id]
                : gekozen.filter((x) => x !== m.id))} />
            <span style={{ opacity: 0.8 }}>{m.bestandsnaam}</span>
          </label>
        ))}
      </div>
    );
  } else if (veld.soort === "paren") {
    veldElement = <Paren veld={veld} waarde={waarde} onChange={onChange} />;
  } else {
    veldElement = (
      <input value={waarde ?? ""} data-testid={testId} onChange={(e) => onChange(e.target.value)}
        style={{ ...veldStijl, minHeight: 48 }} />
    );
  }

  return (
    <div style={{ marginBottom: 34, paddingBottom: 22, borderBottom: "1px solid var(--line)" }}>
      <Label veld={veld} />
      {veldElement}
      <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <span style={{ fontFamily: "var(--sans)", fontSize: 12, opacity: 0.5, maxWidth: "60ch" }}>{veld.hint || ""}</span>
        <Teller veld={veld} waarde={waarde} />
      </div>
      {letOp.map((a) => (
        <Melding key={a.fragment} soort="let-op" testId={`afgestemd-${veld.naam}`}>
          <strong>Let op — afgestemde tekst.</strong> {a.uitleg}
        </Melding>
      ))}
      {fouten.map((v) => (
        <Melding key={v.fout} soort="fout" testId={`verboden-${veld.naam}`}>
          <strong>‘{v.fout}’</strong> mag niet. Gebruik: {v.goed}.
        </Melding>
      ))}
    </div>
  );
}

export default function BlockEditPage() {
  const { blockId } = useParams();
  const navigate = useNavigate();
  const [block, setBlock] = useState(null);
  const [schema, setSchema] = useState(null);
  const [site, setSite] = useState(null);
  const [inhoud, setInhoud] = useState({});
  const [fout, setFout] = useState("");
  const [bezig, setBezig] = useState(false);
  const [opgeslagen, setOpgeslagen] = useState(false);
  const [revisies, setRevisies] = useState([]);
  const [staat, setStaat] = useState(null);
  const [historieOpen, setHistorieOpen] = useState(false);

  const laadStatus = () => Promise.all([
    api.get(`/admin/blocks/${blockId}/revisies`),
    api.get("/admin/publicatie"),
  ]).then(([r, p]) => {
    setRevisies(r.data);
    setStaat((p.data.blokken || []).find((b) => b.id === blockId) || null);
  }).catch(() => {});

  useEffect(() => {
    document.body.classList.add("swof");
    return () => document.body.classList.remove("swof");
  }, []);

  useEffect(() => {
    Promise.all([
      api.get(`/admin/blocks/${blockId}`),
      api.get("/admin/schema"),
      api.get("/admin/site"),
    ]).then(([b, s, c]) => {
      setBlock(b.data);
      setInhoud(b.data.inhoud || {});
      setSchema(s.data);
      setSite(c.data);
      laadStatus();
    }).catch((e) => setFout(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [blockId]);

  const velden = useMemo(
    () => (schema && block ? schema.blocks[block.type] || [] : []),
    [schema, block]
  );

  const opslaan = async () => {
    setBezig(true);
    setFout("");
    try {
      const { data } = await api.put(`/admin/blocks/${blockId}`, { inhoud });
      setBlock(data);
      setInhoud(data.inhoud);
      await laadStatus();
      setOpgeslagen(true);
      setTimeout(() => setOpgeslagen(false), 2500);
    } catch (e) {
      setFout(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBezig(false);
    }
  };

  const publiceren = async () => {
    if (!window.confirm("Dit blok publiceren?\n\nDe bezoeker ziet dan deze versie. De rest van de site blijft zoals hij nu live staat.")) return;
    setBezig(true);
    setFout("");
    try {
      await api.post(`/admin/publiceer/${blockId}`);
      await laadStatus();
    } catch (e) {
      setFout(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBezig(false);
    }
  };

  const terugzetten = async (r) => {
    if (!window.confirm(`De versie van ${new Date(r.bewaard_op).toLocaleString("nl-NL")} terugzetten?\n\nDe huidige tekst wordt eerst in de geschiedenis bewaard.`)) return;
    setBezig(true);
    setFout("");
    try {
      const { data } = await api.post(`/admin/blocks/${blockId}/revisies/${r.id}/terugzetten`);
      setBlock(data);
      setInhoud(data.inhoud);
      await laadStatus();
    } catch (e) {
      setFout(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBezig(false);
    }
  };

  if (fout && !block) {
    return <AdminShell title="Blok bewerken"><Melding soort="fout" testId="blok-fout">{fout}</Melding></AdminShell>;
  }
  if (!block || !schema || !site) {
    return <AdminShell title="Blok bewerken"><div data-testid="blok-laden" style={{ opacity: 0.5 }}>Laden…</div></AdminShell>;
  }

  const acties = (
    <>
      <Link to="/admin" data-testid="blok-terug" style={{
        fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
        color: "rgba(243,234,215,0.7)", border: "1px solid var(--line)", padding: "10px 16px",
      }}>← Pagina</Link>
      <Link to="/voorbeeld" data-testid="blok-voorbeeld" style={{
        fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
        color: "var(--gold)", border: "1px solid rgba(201,169,97,0.4)", padding: "10px 16px",
      }}>Voorbeeld</Link>
    </>
  );

  return (
    <AdminShell title={`Blok ${String(block.positie).padStart(2, "0")} · ${schema.type_labels[block.type] || block.type}`} actions={acties}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 24, flexWrap: "wrap" }}>
        <h1 data-testid="blok-titel" style={{
          fontFamily: "var(--serif)", fontSize: "clamp(30px, 3.6vw, 46px)", fontWeight: 400,
          margin: "0 0 10px", letterSpacing: "-0.02em",
        }}>{schema.type_labels[block.type] || block.type}</h1>
        <span data-testid="blok-gewijzigd" style={{
          fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.16em",
          textTransform: "uppercase", opacity: 0.55,
        }}>
          {block.bijgewerkt_door_naam
            ? `Laatst gewijzigd door ${block.bijgewerkt_door_naam} · ${new Date(block.bijgewerkt_op).toLocaleString("nl-NL")}`
            : "Nog niet gewijzigd sinds het inrichten"}
        </span>
      </div>
      <p style={{ fontFamily: "var(--sans)", fontSize: 14, opacity: 0.6, margin: "0 0 36px", maxWidth: "64ch" }}>
        De stijl komt uit het ontwerp: vet, cursief, tussenkop, opsomming en link. Geen kleuren of
        lettergroottes. Wijzigingen komen in het concept; met <em>Voorbeeld</em> ziet u het resultaat.
        {staat && (
          <span data-testid="blok-staat" style={{ display: "block", marginTop: 10, fontFamily: "var(--mono)", fontSize: 11, color: staat.staat === "gelijk" ? "rgba(243,234,215,0.6)" : "var(--gold)" }}>
            {staat.staat === "gelijk"
              ? "Dit blok staat live zoals het hier staat."
              : "Deze versie staat nog niet live — hij wacht op publicatie."}
          </span>
        )}
      </p>

      {velden.map((veld) => (
        <Veld key={veld.naam} veld={veld} waarde={inhoud[veld.naam]}
          onChange={(v) => setInhoud((h) => ({ ...h, [veld.naam]: v }))}
          media={site.media} videos={site.videos}
          afgestemd={schema.afgestemd} verboden={schema.verboden} />
      ))}

      {fout && <Melding soort="fout" testId="blok-fout">{fout}</Melding>}

      <div style={{ marginTop: 40, borderTop: "1px solid var(--line)", paddingTop: 22 }}>
        <button onClick={() => setHistorieOpen((o) => !o)} data-testid="blok-historie-knop" style={{
          fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
          color: "var(--gold)", background: "transparent", border: "1px solid rgba(201,169,97,0.4)",
          padding: "10px 16px",
        }}>
          {historieOpen ? "Geschiedenis verbergen" : `Geschiedenis (${revisies.length})`}
        </button>
        {historieOpen && (
          <ul data-testid="blok-revisies" style={{
            listStyle: "none", padding: 0, margin: "20px 0 0",
            borderTop: "1px solid var(--line)",
          }}>
            {revisies.length === 0 && (
              <li data-testid="blok-revisies-leeg" style={{
                padding: "14px 4px", fontFamily: "var(--sans)", fontSize: 13, opacity: 0.6,
              }}>Nog geen eerdere versies. Vanaf de eerste wijziging bewaren we de laatste tien.</li>
            )}
            {revisies.map((r) => (
              <li key={r.id} data-testid={`blok-revisie-${r.id}`} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
                padding: "13px 4px", borderBottom: "1px solid var(--line)",
              }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: 11 }}>
                  {new Date(r.bewaard_op).toLocaleString("nl-NL")}
                  <span style={{ opacity: 0.55 }}> · {r.door_naam}</span>
                </span>
                <button onClick={() => terugzetten(r)} disabled={bezig}
                  data-testid={`blok-revisie-terug-${r.id}`} style={{
                    fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.16em",
                    textTransform: "uppercase", color: "var(--gold)", background: "transparent",
                    border: "1px solid rgba(201,169,97,0.4)", padding: "8px 12px",
                  }}>Terugzetten</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{
        position: "sticky", bottom: 0, background: "var(--ink)", paddingTop: 18, paddingBottom: 18,
        borderTop: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap",
      }}>
        <button onClick={opslaan} disabled={bezig} data-testid="blok-opslaan" style={{
          padding: "14px 30px", background: bezig ? "rgba(201,169,97,0.5)" : "var(--gold)",
          color: "var(--ink)", fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.22em",
          textTransform: "uppercase", border: "none", cursor: bezig ? "wait" : "pointer",
        }}>{bezig ? "Opslaan…" : "Opslaan"}</button>
        <button onClick={publiceren} disabled={bezig || staat?.staat === "gelijk"}
          data-testid="blok-publiceren" style={{
            fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
            color: staat?.staat === "gelijk" ? "rgba(243,234,215,0.45)" : "var(--gold)",
            border: `1px solid ${staat?.staat === "gelijk" ? "var(--line)" : "rgba(201,169,97,0.6)"}`,
            padding: "13px 20px", background: "transparent",
          }}>
          {staat?.staat === "gelijk" ? "Al gepubliceerd" : "Dit blok publiceren"}
        </button>
        <button onClick={() => navigate("/admin")} data-testid="blok-annuleren" style={{
          fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
          color: "rgba(243,234,215,0.7)", border: "1px solid var(--line)", padding: "13px 20px",
          background: "transparent",
        }}>Terug naar de pagina</button>
        {opgeslagen && (
          <span data-testid="blok-opgeslagen" style={{
            fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em",
            textTransform: "uppercase", color: "var(--gold)",
          }}>Opgeslagen in het concept ✓</span>
        )}
      </div>
    </AdminShell>
  );
}
